// Files out of a drag-and-drop, folders included. A drop's DataTransfer only
// lists the top-level items; a dropped folder is walked through the File and
// Directory Entries API (webkitGetAsEntry, every current browser), recursively.
// Browsers without it get the flat dataTransfer.files.

// Files an OS or an editor leaves next to real ones: never meant to be uploaded.
const JUNK_NAMES = new Set(['.ds_store', 'thumbs.db', 'ehthumbs.db', 'desktop.ini'])
// Folders of such files (macOS zip resource forks).
const JUNK_FOLDERS = new Set(['__macosx'])

/**
 * Whether a file name is OS / editor clutter (.DS_Store, Thumbs.db,
 * desktop.ini, any dotfile, an Office lock file "~$name.docx"). Only the last
 * path segment counts.
 */
export function isJunkFile(name) {
  const base = String(name ?? '')
    .split(/[\\/]/)
    .pop()
    .trim()
    .toLowerCase()
  return !base || base.startsWith('.') || base.startsWith('~$') || JUNK_NAMES.has(base)
}

function isJunkFolder(name) {
  const base = String(name ?? '').trim().toLowerCase()
  return base.startsWith('.') || JUNK_FOLDERS.has(base)
}

// A directory reader answers in batches (Chrome: 100 entries) until an empty one.
function readAllEntries(reader) {
  return new Promise((resolve, reject) => {
    const all = []
    const next = () =>
      reader.readEntries((batch) => {
        if (!batch.length) resolve(all)
        else {
          all.push(...batch)
          next()
        }
      }, reject)
    next()
  })
}

function fileOf(entry) {
  return new Promise((resolve, reject) => entry.file(resolve, reject))
}

/**
 * Every file of a drop, folders walked recursively, junk skipped.
 *
 * Call it INSIDE the drop handler: the items of a DataTransfer are only
 * readable during the event, so they are taken synchronously here (before
 * the first await); only the folder walk is asynchronous.
 *
 * @param {DataTransfer} dataTransfer
 * @param {{ max?: number }} [opts]  stop after `max` files (truncated: true)
 * @returns {Promise<{ files: File[], truncated: boolean, unreadable: number }>}
 *   unreadable: files the browser could not read (permissions, a file removed
 *   meanwhile), skipped.
 */
export function collectDroppedFiles(dataTransfer, { max = Infinity } = {}) {
  const picks = []
  for (const item of Array.from(dataTransfer?.items || [])) {
    if (!item || item.kind !== 'file') continue
    const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null
    if (entry) picks.push({ entry })
    else {
      const file = typeof item.getAsFile === 'function' ? item.getAsFile() : null
      if (file) picks.push({ file })
    }
  }
  // No items list (an old browser): the flat file list.
  if (!picks.length) Array.from(dataTransfer?.files || []).forEach((file) => picks.push({ file }))

  const files = []
  let truncated = false
  let unreadable = 0

  const add = (file) => {
    if (isJunkFile(file?.name)) return
    if (files.length >= max) {
      truncated = true
      return
    }
    files.push(file)
  }

  const walk = async (entry) => {
    if (truncated) return
    if (entry.isFile) {
      if (isJunkFile(entry.name)) return
      if (files.length >= max) {
        truncated = true
        return
      }
      try {
        files.push(await fileOf(entry))
      } catch {
        unreadable += 1
      }
      return
    }
    if (!entry.isDirectory || isJunkFolder(entry.name)) return
    let children
    try {
      children = await readAllEntries(entry.createReader())
    } catch {
      unreadable += 1
      return
    }
    for (const child of children) {
      // eslint-disable-next-line no-await-in-loop
      await walk(child)
      if (truncated) return
    }
  }

  return (async () => {
    for (const pick of picks) {
      if (pick.file) add(pick.file)
      // eslint-disable-next-line no-await-in-loop
      else await walk(pick.entry)
      if (truncated) break
    }
    return { files, truncated, unreadable }
  })()
}

/**
 * The files of an <input type="file"> (multiple, or a folder picked with
 * webkitdirectory), junk skipped, at most `max`.
 * @returns {{ files: File[], truncated: boolean }}
 */
export function pickedFiles(fileList, { max = Infinity } = {}) {
  const files = []
  let truncated = false
  for (const file of Array.from(fileList || [])) {
    if (isJunkFile(file?.name) || isJunkFolderPath(file?.webkitRelativePath)) continue
    if (files.length >= max) {
      truncated = true
      break
    }
    files.push(file)
  }
  return { files, truncated }
}

// A folder pick lists nested files with their path (webkitRelativePath):
// skip the ones inside a junk folder (__MACOSX, .git, ...).
function isJunkFolderPath(path) {
  if (!path) return false
  const folders = String(path).split('/').slice(0, -1)
  return folders.some((folder) => isJunkFolder(folder))
}
